/* eslint-disable no-console */
import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Banner } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import {
  FormFieldDefinition,
  FormFieldRadio
} from '@publishing-engine/domain/types/article';
import { RadioGroup } from '../../../RadioGroup';
import { generateValidationSchema } from '../../../../utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FieldLabel } from '../../../Field';

interface Props {
  isOpen: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  formField: FormFieldRadio;
  value: string;
  showCopyrightWarning?: boolean;
  customCopies?: {
    fieldLabel?: string;
  };
}

type IRadioFormState = {
  [key: string]: string;
};

const RadioUpdateModal: React.FC<Props> = props => {
  const {
    isOpen,
    onSubmit,
    onCancel,
    formField,
    value,
    showCopyrightWarning,
    customCopies
  } = props;

  const defaultValues = _.set(
    {} as IRadioFormState,
    formField.caseDataFields.main,
    props.value
  );

  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, control, watch } = methods;

  const fieldName = formField.caseDataFields.main;
  const mainField = `${fieldName}` as const;
  const fieldValue = watch(mainField);

  const options: {
    value: string;
    children: React.ReactNode;
  }[] = Object.values(formField.params.validations.options).map(option => {
    return {
      value: option,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="headingItem">
            {formField.params.labels.options[option]?.main}
          </Text>
          <Text textStyle="textBody">
            {formField.params.labels.options[option]?.secondary}
          </Text>
        </Flex>
      )
    };
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        padding={8}
        gap={4}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <form
          onSubmit={handleSubmit(() => {
            onSubmit(fieldValue);
          })}
        >
          <Flex marginBottom={4}>
            <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
          </Flex>

          <Flex gap={4} flexDirection="column" marginBottom={4}>
            {showCopyrightWarning && (
              <Banner
                variant="attention"
                heading="Data Requirements"
                content="Updating the publishing model will invalidate the existing copyright form. A new copyright form must be provided."
              />
            )}

            {customCopies?.fieldLabel && (
              <FieldLabel
                isRequired={formField.params.validations.required}
                label={customCopies?.fieldLabel || formField.params.labels.main}
                subText=""
              />
            )}

            <RadioGroup
              control={control}
              defaultValue={value}
              name={fieldName}
              options={options}
            />
          </Flex>

          <Flex
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
          >
            <Button variant="secondary" onClick={onCancel} borderRadius="small">
              Cancel
            </Button>
            <Button variant="primary" type="submit" borderRadius="small">
              Save Changes
            </Button>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};

export default RadioUpdateModal;
